import { useApolloClient } from "@apollo/client";
import { useState } from "react";
import type { Holiday } from "@models/ISchema";
import type { Holidays } from "@services/types/holidays/holidaysTypes";
import HOLIDAYS_QUERY from "./graphql/holidays.query.graphql";

interface UseGetHolidaysProps {
  scenario: string;
}

type UseGetHolidays = [
  {
    isLoading: boolean;
    holidays: Holidays;
    isLoaded: boolean;
    error: Error | null;
  },
  () => Promise<void>,
];

/**
 * Fetches scenario holidays and exposes them as a date-indexed map.
 * Never throws: failures are surfaced through the `error` flag so callers can
 * fire it from an effect without wiring their own rejection handling.
 */
export const useGetHolidays = (props: UseGetHolidaysProps): UseGetHolidays => {
  const { scenario } = props;
  const client = useApolloClient();

  const [isLoading, setIsLoading] = useState(false);
  const [holidays, setHolidays] = useState<Holidays>({});
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const getHolidays = async () => {
    setIsLoading(true);
    setIsLoaded(false);
    setError(null);

    try {
      const { data } = await client.query({
        query: HOLIDAYS_QUERY,
        variables: {
          scenarioId: scenario,
        },
      });

      const holidaysObj: Holidays = {};

      (data?.data?.holidays ?? []).forEach((holiday: Holiday) => {
        if (holiday?.date) holidaysObj[holiday.date] = holiday;
      });

      setHolidays(holidaysObj);
      setIsLoaded(true);
    } catch (err) {
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  };

  return [
    {
      holidays,
      isLoading,
      isLoaded,
      error,
    },
    getHolidays,
  ];
};
